"use client";

import {
  Check,
  ChevronDown,
  ChevronRight,
  CircleAlert,
  Loader2,
  RefreshCw,
  Wrench,
} from "lucide-react";
import { useMemo, useState } from "react";

import { cn } from "@/shared/lib/utils/cn";
import { formatElapsedMs } from "./format-elapsed";
import {
  activeToolLabel,
  toolActivityRowLabel,
  type CoalescedToolActivity,
} from "./group-tool-stack";
import { InsightsToolkitLogo } from "./InsightsToolkitLogo";
import { toolActivityPlatforms } from "./tool-activity-summary";

function ToolDetail({ activity }: { activity: CoalescedToolActivity }) {
  const payload =
    activity.result !== undefined
      ? activity.result
      : activity.args !== undefined
        ? activity.args
        : null;
  if (payload === null && !activity.softRefuseHint) return null;
  return (
    <div className="mb-2 space-y-1.5">
      {activity.softRefuseHint ? (
        <p className="px-1 text-[11px] text-muted-foreground">{activity.softRefuseHint}</p>
      ) : null}
      {payload !== null ? (
        <pre className="max-h-40 overflow-auto rounded-lg bg-muted/50 px-3 py-2 font-mono text-[11px] leading-4 break-words whitespace-pre-wrap text-muted-foreground [overflow-wrap:anywhere]">
          {JSON.stringify(payload, null, 2)}
        </pre>
      ) : null}
    </div>
  );
}

function StatusIcon({ activity }: { activity: CoalescedToolActivity }) {
  if (activity.pending) {
    return <Loader2 className="size-3 shrink-0 animate-spin text-primary" />;
  }
  if (activity.retried) {
    return <RefreshCw className="size-3 shrink-0 text-muted-foreground" />;
  }
  if (activity.softRefuse) {
    return <CircleAlert className="size-3 shrink-0 text-amber-600" />;
  }
  if (activity.error) {
    return <CircleAlert className="size-3 shrink-0 text-destructive" />;
  }
  return <Check className="size-3 shrink-0 text-foreground/50" />;
}

function PlatformLogos({ activities }: { activities: CoalescedToolActivity[] }) {
  const platforms = useMemo(() => {
    const seen = new Set<string>();
    for (const a of activities) {
      for (const p of toolActivityPlatforms(a.tool, a.args)) seen.add(p);
    }
    return [...seen].slice(0, 3);
  }, [activities]);

  if (!platforms.length) return null;
  return (
    <span className="flex shrink-0 items-center gap-0.5">
      {platforms.map((p) => (
        <span
          key={p}
          className="flex size-4 items-center justify-center rounded-full bg-muted/80"
          title={p}
        >
          <InsightsToolkitLogo system={p} className="size-3 object-contain" />
        </span>
      ))}
    </span>
  );
}

/** Accordion stack of sequential tool calls (no agent text between them). */
export function InsightsToolStack({
  activities,
  live = false,
}: {
  activities: CoalescedToolActivity[];
  live?: boolean;
}) {
  const [expanded, setExpanded] = useState(false);
  const [detailId, setDetailId] = useState<string | null>(null);

  if (!activities.length) return null;

  const pending = activities.some((a) => a.pending);
  const hasError = activities.some((a) => a.error && !a.retried);
  const header = expanded
    ? `${activities.length} tool${activities.length === 1 ? "" : "s"}`
    : activeToolLabel(activities);

  return (
    <div
      className={cn(
        "w-full min-w-0 overflow-hidden rounded-tl-md rounded-tr-2xl rounded-b-2xl border border-border/70 bg-card text-[12px] shadow-sm",
        (pending || live) && "border-primary/35 ring-1 ring-primary/10",
      )}
    >
      <button
        type="button"
        className="flex w-full items-center gap-2 px-3 py-2 text-left text-muted-foreground"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        {hasError ? (
          <CircleAlert className="size-3.5 shrink-0 text-destructive" />
        ) : pending || live ? (
          <Loader2 className="size-3.5 shrink-0 animate-spin text-primary" />
        ) : (
          <Check className="size-3.5 shrink-0 text-foreground/70" />
        )}
        <PlatformLogos activities={activities} />
        <span className="min-w-0 flex-1 truncate font-medium text-foreground/80">
          {header}
          {!expanded && pending ? (
            <span className="ml-1 font-normal text-muted-foreground">· running</span>
          ) : null}
        </span>
        <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
          {activities.length}
        </span>
        {expanded ? (
          <ChevronDown className="size-3.5 shrink-0" />
        ) : (
          <ChevronRight className="size-3.5 shrink-0" />
        )}
      </button>

      {expanded ? (
        <div className="border-t border-border/50 px-2 py-1">
          {activities.map((activity) => {
            const open = detailId === activity.id;
            const hasDetails =
              activity.args !== undefined ||
              activity.result !== undefined ||
              Boolean(activity.softRefuseHint);
            const rowPlatforms = toolActivityPlatforms(activity.tool, activity.args);
            return (
              <div key={activity.id} className="border-b border-border/35 last:border-0">
                <button
                  type="button"
                  disabled={!hasDetails}
                  className="flex w-full items-center gap-2 px-1 py-2 text-left disabled:cursor-default"
                  onClick={() => setDetailId(open ? null : activity.id)}
                >
                  {rowPlatforms[0] ? (
                    <span className="flex size-4 shrink-0 items-center justify-center rounded-full bg-muted/80">
                      <InsightsToolkitLogo
                        system={rowPlatforms[0]}
                        className="size-3 object-contain"
                      />
                    </span>
                  ) : (
                    <Wrench
                      className={cn(
                        "size-3.5 shrink-0",
                        activity.error
                          ? "text-destructive"
                          : activity.pending
                            ? "text-primary"
                            : "text-muted-foreground",
                      )}
                    />
                  )}
                  <span className="min-w-0 flex-1 truncate text-foreground/80">
                    {toolActivityRowLabel(activity)}
                    {activity.retried ? (
                      <span className="ml-1 font-normal text-muted-foreground">· retried</span>
                    ) : null}
                    {activity.softRefuse ? (
                      <span className="ml-1 font-normal text-muted-foreground">· blocked</span>
                    ) : null}
                  </span>
                  {activity.durationMs != null && !activity.pending ? (
                    <span className="shrink-0 text-[10px] tabular-nums text-muted-foreground">
                      {formatElapsedMs(activity.durationMs)}
                    </span>
                  ) : null}
                  <StatusIcon activity={activity} />
                  {hasDetails ? (
                    open ? (
                      <ChevronDown className="size-3 shrink-0 text-muted-foreground" />
                    ) : (
                      <ChevronRight className="size-3 shrink-0 text-muted-foreground" />
                    )
                  ) : null}
                </button>
                {open ? <ToolDetail activity={activity} /> : null}
              </div>
            );
          })}
        </div>
      ) : null}
    </div>
  );
}
